<template>
  <b-row>
    <b-col>
      <h2>Application for Employment</h2>
      <p>
          Equal access to programs, services and employment is available to all persons. Those applicants 
          requiring reasonable accommodation to the application and/or interview process should notify a 
          representative of the Human Resources Department. 
      </p>
      <b-form ref="applicationForm" @submit="onSubmit">
        <b-card title="General Information">
          <b-form-group label-cols-md="3" label="Position(s) applied for:" label-for="form.positions">
            <b-input id="form.positions" trim required v-model="form.positions" placeholder="What positions(s) are you applying for?"/>
          </b-form-group>
          <b-form-group label-cols-md="3" label="First Name:" label-for="form.firstName">
            <b-input id="form.firstName" trim required v-model="form.firstName"/>
          </b-form-group>
          <b-form-group label-cols-md="3" label="Middle Name:" label-for="form.middleName">
            <b-input id="form.middleName" trim v-model="form.middleName"/>
          </b-form-group>
          <b-form-group label-cols-md="3" label="Last Name:" label-for="form.lastName">
            <b-input id="form.lastName" trim required v-model="form.lastName"/>
          </b-form-group>
          <b-form-group label-cols-md="3" label="Date of Birth:" label-for="form.dob">
            <b-input id="form.dob" type="date" required v-model="form.dob"/>
          </b-form-group>
          <b-card v-if="age < 18 && age != -1" title="Additional Questions for Minors">
            <b-form-group label-cols-lg="6" label="Given your DOB you are under 18, can you furnish a work permit?" label-for="form.under18WorkPermit">
              <b-form-radio-group id="form.under18WorkPermit" v-model="form.under18WorkPermit">
                <b-form-radio :value="true">Yes</b-form-radio>
                <b-form-radio :value="false">No</b-form-radio>
              </b-form-radio-group>
            </b-form-group>
            <b-form-group v-if="form.under18WorkPermit == false" label="Please explain why you cannot furnish a work permit:" label-for="form.noUnder18WorkPermitExplain">
              <b-textarea id="form.noUnder18WorkPermitExplain" rows="3" trim v-model="form.noUnder18WorkPermitExplain"/>
            </b-form-group>
          </b-card>
          <b-form-group label-cols-md="3" label="Social Security Number:" label-for="form.ssn">
            <b-input id="form.ssn" trim number required v-model="form.ssn" :state="ssnState" placeholder="ex. 111223333"/>
            <b-form-invalid-feedback :state="ssnState">
              SSN must be 9 characters
            </b-form-invalid-feedback>          
          </b-form-group>
          <b-form-group label-cols-md="3" label="Address:" label-for="form.address">
            <b-input id="form.address" trim required v-model="form.address"/>
          </b-form-group>
          <b-form-group label-cols-md="3" label="City:" label-for="form.city">
            <b-input id="form.city" trim required v-model="form.city"/>
          </b-form-group>
          <b-form-group label-cols-md="3" label="State:" label-for="form.state">
            <b-input id="form.state" trim required v-model="form.state"/>
          </b-form-group>
          <b-form-group label-cols-md="3" label="Zip Code:" label-for="form.zip">
            <b-input id="form.zip" trim type="number" required v-model="form.zip"/>
          </b-form-group>
          <b-form-group label-cols-md="3" label="Phone:" label-for="form.phone">
            <b-input id="form.phone" trim number required v-model="form.phone" :state="phoneState" placeholder="ex. 5551119999"/>
            <b-form-invalid-feedback :state="phoneState">
              Phone must be 10 characters
            </b-form-invalid-feedback>          
          </b-form-group>
          <b-form-group label-cols-md="3" label="Secondary Phone:" label-for="form.otherPhone">
            <b-input id="form.otherPhone" trim number v-model="form.otherPhone" :state="otherPhoneState" placeholder="ex. 5551119999"/>
            <b-form-invalid-feedback :state="otherPhoneState">
              Secondary Phone must be 10 characters
            </b-form-invalid-feedback>          
          </b-form-group>
          <b-form-group label-cols-md="3" label="Email:" label-for="form.email">
            <b-input id="form.email" trim type="email" required v-model="form.email"/>
          </b-form-group>
          <b-form-group label-cols-md="3" label="How did you hear of us?" label-for="form.howHeardOfUs">
            <b-input id="form.howHeardOfUs" trim type="text" required v-model="form.howHeardOfUs"/>
          </b-form-group>
          <b-form-group label-cols-md="3" label="Have you ever worked here before?" label-for="form.employedBefore">
            <b-form-radio-group required id="form.employedBefore" v-model="form.employedBefore">
              <b-form-radio :value="true">Yes</b-form-radio>
              <b-form-radio :value="false">No</b-form-radio>
            </b-form-radio-group>
          </b-form-group>
          <b-form-group label-cols-md="3" v-if="form.employedBefore" label="Please give positions and dates:" label-for="form.employedBeforeSummary">
            <b-textarea id="form.employedBeforeSummary" rows="3" trim v-model="form.employedBeforeSummary"/>
          </b-form-group>
          <b-form-group label-cols-md="3" label="Are you eligible to work in the United States?" label-for="form.eligibleToWorkInUSA">
            <b-form-radio-group required id="form.eligibleToWorkInUSA" v-model="form.eligibleToWorkInUSA">
              <b-form-radio :value="true">Yes</b-form-radio>
              <b-form-radio :value="false">No</b-form-radio>
            </b-form-radio-group>
          </b-form-group>
          <b-form-group label-cols-md="3" label="Date available for work:" label-for="form.dateAvailableToStart">
            <b-input id="form.dateAvailableToStart" type="date" required v-model="form.dateAvailableToStart"/>
          </b-form-group>
          <b-form-group label-cols-md="3" label="What is your desired salary range?" label-for="form.desiredSalaryRange">
            <b-input id="form.desiredSalaryRange" trim required v-model="form.desiredSalaryRange"/>
          </b-form-group>
          <b-form-group label-cols-md="3" label="Types of employment desired:">
            <b-form-checkbox-group id="form.typesOfEmployment" v-model="form.typesOfEmployment" name="typesOfEmployment">
              <b-form-checkbox value="Full Time">Full Time</b-form-checkbox>
              <b-form-checkbox value="Part Time">Part Time</b-form-checkbox>
              <b-form-checkbox value="Temporary">Temporary</b-form-checkbox>
              <b-form-checkbox value="Seasonal">Seasonal</b-form-checkbox>
              <b-form-checkbox value="Educational Co-op">Educational Co-Op</b-form-checkbox>
            </b-form-checkbox-group>
          </b-form-group>
          <b-form-group label-cols-md="3" label="Shift preferences:">
            <b-form-checkbox-group id="form.shiftPreferences" v-model="form.shiftPreferences" name="shiftPreferences">
              <b-form-checkbox value="Any">Any</b-form-checkbox>
              <b-form-checkbox value="1st">1st</b-form-checkbox>
              <b-form-checkbox value="2nd">2nd</b-form-checkbox>
              <b-form-checkbox value="3rd">3rd</b-form-checkbox>
            </b-form-checkbox-group>
          </b-form-group>
          <b-form-group label-cols-md="3" label="Are you able to meet the attendence requirements of this postition?" label-for="form.canMeetAttendence">
            <b-form-radio-group required id="form.canMeetAttendence" v-model="form.canMeetAttendence">
              <b-form-radio :value="true">Yes</b-form-radio>
              <b-form-radio :value="false">No</b-form-radio>
            </b-form-radio-group>
          </b-form-group>
          <b-form-group label-cols-md="3" label="Have you ever pled 'guilty' or 'no contest' or been convicted of a crime?" label-for="form.hasCommitedCrime">
            <b-form-radio-group required id="form.hasCommitedCrime" v-model="form.hasCommitedCrime">
              <b-form-radio :value="true">Yes</b-form-radio>
              <b-form-radio :value="false">No</b-form-radio>
            </b-form-radio-group>
            <b-form-text>Answering 'yes' to this question does not constitute an automatic bar to employment. Factors such as date of the offense, seriousness and the nature of the violation, rehabilitation and position applied for will be taken into account.</b-form-text>
          </b-form-group>
          <b-form-group label-cols-md="3" v-if="form.hasCommitedCrime" label="Please provide dates and details:" label-for="form.crimeDetails">
            <b-textarea id="form.crimeDetails" rows="3" trim v-model="form.crimeDetails"/>
          </b-form-group>          
          <b-form-group label-cols-md="3" label="Do you have a valid checking or savings account?" label-for="form.validCheckingOrSavings">
            <b-form-radio-group required id="form.validCheckingOrSavings" v-model="form.validCheckingOrSavings">
              <b-form-radio :value="true">Yes</b-form-radio>
              <b-form-radio :value="false">No</b-form-radio>
            </b-form-radio-group>
          </b-form-group>
          <b-form-group label-cols-md="3" label="Driver's license number if driving is an essential job function:" label-for="form.driversLicenseNumber">
            <b-input id="form.driversLicenseNumber" trim v-model="form.driversLicenseNumber"/>
          </b-form-group>
          <b-form-group label-cols-md="3" label="Driver's license state:" label-for="form.driversLicenseState">
            <b-input id="form.driversLicenseState" trim v-model="form.driversLicenseState"/>
          </b-form-group>
        </b-card>
        <b-card title="Skills and Qualifications">
          <b-form-group label="Summarize any training, skills, licenses and/or certificates that may qualify you as being able to perform job-related functions in the position for which you are applying:" label-for="form.skillsAndQualifications">
            <b-textarea rows="3" id="form.skillsAndQualifications" required trim v-model="form.skillsAndQualifications"/>
          </b-form-group>
          <b-card title="Educational Background">
            <b-row v-for="school in form.schooling" :key="school.type" no-gutters>
              <b-col md>{{school.type}}</b-col>
              <b-col md="2"><b-input trim v-model="school.name" placeholder="Name"/></b-col>
              <b-col md="2"><b-input trim v-model="school.location" placeholder="Location"/></b-col>
              <b-col md><b-input trim number v-model="school.yearsCompleted" placeholder="Years"/></b-col>
              <b-col md>
                <b-form-checkbox :value="true" v-model="school.graduated">Graduated</b-form-checkbox>
              </b-col>
              <b-col md="2"><b-input trim v-model="school.degree" placeholder="Degree"/></b-col>
              <b-col md="2"><b-input trim v-model="school.major" placeholder="Course of Study/Major"/></b-col>
            </b-row>
          </b-card>
          <b-card title="References">
            <b-row v-for="ref in form.references" :key="ref.id" no-gutters>
              <b-col md>Ref {{ref.id}}</b-col>
              <b-col md="6"><b-input required trim v-model="ref.name" placeholder="Name"/></b-col>
              <b-col md="3"><b-input required trim v-model="ref.phone" placeholder="Phone"/></b-col>
              <b-col md="2"><b-input required number trim v-model="ref.yearsKnown" placeholder="Years Known"/></b-col>
            </b-row>
          </b-card>
        </b-card>
        <b-card title="Employment History" sub-title="Provide the following information of your past four (4) employers, assignments or volunteer activities, starting with the most recent.">
          <template v-for="history in form.employmentHistory">
            <b-card :key="history.id" :sub-title="`Employer ${history.id}`">
              <b-form-group label-cols-md="3" label="From:">
                <b-input type="date" v-model="history.from"/>
              </b-form-group>
              <b-form-group label-cols-md="3" label="To:">
                <b-input type="date" v-model="history.to"/>
              </b-form-group>
              <b-form-group label-cols-md="3" label="Employer:">
                <b-input trim v-model="history.employer"/>
              </b-form-group>
              <b-form-group label-cols-md="3" label="Phone:">
                <b-input trim number v-model="history.phone" placeholder="ex. 5551119999"/>
              </b-form-group>
              <b-form-group label-cols-md="3" label="Address:">
                <b-input trim v-model="history.address"/>
              </b-form-group>
              <b-form-group label-cols-md="3" label="Starting Job Title:">
                <b-input trim v-model="history.startTitle"/>
              </b-form-group>
              <b-form-group label-cols-md="3" label="Ending Job Title:">
                <b-input trim v-model="history.endTitle"/>
              </b-form-group>
              <b-form-group label-cols-md="3" label="Summarize the nature of work performed and responsibilities:">
                <b-textarea rows="3" trim v-model="history.workSummary"/>
              </b-form-group>
              <b-form-group label-cols-md="3" label="Immediate supervisor and title:">
                <b-input trim v-model="history.supervisor"/>
              </b-form-group>
              <b-form-group label-cols-md="3" label="May we contact for reference?">
                <b-form-radio-group v-model="history.canContact">
                  <b-form-radio :value="true">Yes</b-form-radio>
                  <b-form-radio :value="false">No</b-form-radio>
                </b-form-radio-group>
              </b-form-group>
              <b-form-group label-cols-md="3" label="Reason for leaving:">
                <b-input trim v-model="history.reasonForLeaving"/>
              </b-form-group>
              <b-form-group label-cols-md="3" label="Start hourly rate/salary:">
                <b-input trim v-model="history.startRate"/>
              </b-form-group>
              <b-form-group label-cols-md="3" label="Ending hourly rate/salary:">
                <b-input trim v-model="history.endRate"/>
              </b-form-group>
            </b-card>
          </template>
        </b-card>
        <b-card title="Authorization for Background Check" sub-title="Please fill out the following authorization so that we may expedite your application process.">
          <p>
            I, <b>{{ form.firstName }} {{ form.middleName}} {{form.lastName}}</b> hereby authorize {{sourceName}} to investigate my background and qualifications for purposes of evaluating whether I am qualified for the position for which I am applying. I understand that {{sourceName}} will utilize an outside firm or firms to assist it in checking such information, and I specifically authorize such an investigation by information services and outside entities of the company's choice. I also understand that I may withhold my permission and that in such a case, no investigation will be done, and my application for employment will not be processed further.
          </p>
          <b-card title="Please verify your social security number and drivers license">
            <b-form-group label-cols-md="3" label="Social Security Number:" label-for="form.ssn">
              <b-input id="form.ssn" trim number required v-model="form.ssn" :state="ssnState" placeholder="ex. 111223333"/>
              <b-form-invalid-feedback :state="ssnState">
                SSN must be 9 characters
              </b-form-invalid-feedback>          
            </b-form-group>
            <b-form-group label-cols-md="3" label="Driver's license number:" label-for="form.driversLicenseNumber">
              <b-input id="form.driversLicenseNumber" trim v-model="form.driversLicenseNumber"/>
            </b-form-group>
            <b-form-group label-cols-md="3" label="Driver's license state:" label-for="form.driversLicenseState">
              <b-input id="form.driversLicenseState" trim v-model="form.driversLicenseState"/>
            </b-form-group>
            <b-form-group label-cols-md="3" label="Date of Birth:" label-for="form.dob">
              <b-input id="form.dob" type="date" required v-model="form.dob"/>
            </b-form-group>
            <b-card :sub-title="signatureTitle">
              <p>
                <vueSignature ref="drawnSignature" :sigOption="{penColor:'rgb(0, 0, 0)',backgroundColor:'rgb(200,200,200)'}" :w="'600px'" :h="'200px'"></vueSignature> 
                <b-button @click="clear">Clear</b-button>
                </p>
              <b-form-group label-cols-md="3" label="Digital Signature:" label-for="form.signature">
                <b-input id="form.signature" trim required v-model="form.signature" placeholder="Enter your full legal name"/>
              </b-form-group>
            </b-card>
          </b-card>
        </b-card>
        <b-card title="Applicant Statement">
          <p>
              I certify that all information I have provided in order to apply for and secure work with the employer is true, complete and correct. I understand that any information provided by me that is found to be false, incomplete or misrepresented in any respect will be be sufficient cause to (i) cancel further consideration of this application, or (ii) immediately discharge me from the employer's service, whenever it is discovered. 
          </p>
          <p>
              I expressly authorize, without reservation, the employer, its representatives, employees or agents to contact and obtain information from all references (personal and professional), employers, public agencies, licensing authorities and educational institutions and to otherwise verify the accuracy of all information provided by me in this application, resume or job interview. I hereby waive any and all rights and claims I may have regarding the employer, its agents, employees or representatives, for seeking, gathering, and using such information in the employment process and all other persons, corporations or organizations for furnishing such information about me.
          </p>
          <p>
              I understand that the employer does not unlawfully discriminate in employment and no question on this application is used for the purpose of limiting or excusing any applicant from consideration for employment on a basis prohibited by applicable local, state or federal law.
          </p>
          <p>
              I understand that this application remains current for only 30 days. At the conclusion of that time, if I have not heard from the employer and still wish to be considered for employment, it will be necessary to reapply and fill out a new application.
          </p>
          <p>
              If I am hired, I understand that I am free to resign at any time, with or without cause and without prior notice, and the employer reserves the right to terminate my employment at any time, with or without cause and without prior notice, except as may be required by law. This application does not constitute an agreement or contract for employment for any specified period or definite duration. I understand that no supervisor or representative of the employer is authorized to make any assurances to the contrary and that no implied, oral or written agreements contrary to the foregoing express language are valid unless they are in writing and signed by the employer's president. 
          </p>
          <p>
              I also understand that if I am hired, I will be required to provide proof of identity and legal authority to work in the United States and that federal immigration laws require me to complete an I-9 Form in this regard. 
          </p>
          <b-checkbox :value="true" v-model="form.acceptApplicantStatement">I certify that I have read, fully understand, and accept all terms of the foregoing Applicant Statement.</b-checkbox>
          <br/>
          <b-button :disabled="!form.acceptApplicantStatement" type="submit" variant="primary">Apply</b-button>
          &nbsp;
          <b-button type="reset" variant="danger">Reset Form</b-button>
          <b-button v-if="showTestDataButton" type="button" @click="loadTestData" >Test Data</b-button>
        </b-card>
      </b-form>
    </b-col>
    <LoadingSplash :loading="loading"/>
  </b-row>
</template>

<script>
import moment from "moment";
import axios from "axios";
import LoadingSplash from "./LoadingSplash.vue";
import { MAILER_URL } from "../qcfsconfig";
import vueSignature from "vue-signature";

//let str = `{"loading":false,"positions":"positions","firstName":"first","middleName":"middle","lastName":"last","dob":"2008-01-01","ssn":111223333,"address":"addr","city":"city","state":"st","zip":"11111","phone":5551112222,"otherPhone":5553334444,"email":"me@email.com","under18WorkPermit":false,"noUnder18WorkPermitExplain":"reason for no work permit","employedBefore":true,"employedBeforeSummary":"previous pos and date","eligibleToWorkInUSA":true,"dateAvailableToStart":"2020-04-16","desiredSalaryRange":"12/hr or whatever","typesOfEmployment":["Full Time","Part Time","Temporary","Seasonal","Educational Co-op"],"canMeetAttendence":true,"hasCommitedCrime":true,"crimeDetails":"crime dates and details","driversLicenseNumber":"dl12345","driversLicenseState":"st","skillsAndQualifications":"skills and quals","schooling":[{"type":"High School","name":"hs","location":"hs loc","yearsCompleted":4,"graduated":true,"degree":"diploma","major":""},{"type":"College","name":"coll","location":"coll loc","yearsCompleted":4,"graduated":true,"degree":"bachelors","major":"liberal arts"},{"type":"Other","name":"o-coll","location":"o-coll loc","yearsCompleted":2,"graduated":true,"degree":"certificate","major":"trade x"}],"references":[{"id":1,"name":"joe","phone":"1114445555","yearsKnown":3},{"id":2,"name":"bob","phone":"2224445555","yearsKnown":2},{"id":3,"name":"mary","phone":"3334445555","yearsKnown":1}],"employmentHistory":[{"id":1,"from":"2019-01-01","to":"2019-12-01","employer":"emp 1","phone":1118889999,"address":"emp 1 addr","startTitle":"1 start title","endTitle":"1 end title","workSummary":"job 1 summary","supervisor":"job 1 super","canContact":true,"reasonForLeaving":"job 1 reason","startRate":"job 1 start rate","endRate":"job 1 end rate"},{"id":2,"from":"2018-01-01","to":"2018-12-01","employer":"emp 2","phone":2227778888,"address":"emp 2 addr","startTitle":"2 start title","endTitle":"2 end title","workSummary":"job 2 summary","supervisor":"job 2 super","canContact":true,"reasonForLeaving":"job 2 reason","startRate":"job 2 start rate","endRate":"job 2 end rate"},{"id":3,"from":"2017-01-01","to":"2017-12-01","employer":"emp 3","phone":3337778888,"address":"emp 3 addr","startTitle":"3 start title","endTitle":"3 end title","workSummary":"job 3 summary","supervisor":"job 3 super","canContact":true,"reasonForLeaving":"job 3 reason","startRate":"job 3 start rate","endRate":"job 3 end rate"},{"id":4,"from":"2015-01-01","to":"2015-12-01","employer":"emp 4","phone":4447778888,"address":"emp 4 addr","startTitle":"4 start title","endTitle":"4 end title","workSummary":"job 4 summary","supervisor":"job 4 super","canContact":true,"reasonForLeaving":"job 4 reason","startRate":"job 4 start rate","endRate":"job 4 end rate"}],"acceptApplicantStatement":true}`;
//let json = JSON.parse(str);

export default {
  components: {
    LoadingSplash,
    vueSignature
  },
  props: {
    source: String,
    sourceName: String,
  },
  data() {
    return {
      loading: false,
      form: {
        positions: null,
        firstName: null,
        middleName: null,
        lastName: null,
        dob: null,
        ssn: null,
        address: null,
        city: null,
        state: null,
        zip: null,
        phone: null,
        otherPhone: null,
        email: null,
        howHeardOfUs: null,
        under18WorkPermit: true,
        noUnder18WorkPermitExplain: null,
        employedBefore: false,
        employedBeforeSummary: null,
        eligibleToWorkInUSA: true,
        dateAvailableToStart: null,
        desiredSalaryRange: null,
        typesOfEmployment: null,
        shiftPreferences: null,
        canMeetAttendence: true,
        hasCommitedCrime: false,
        crimeDetails: null,
        validCheckingOrSavings: true,
        driversLicenseNumber: null,
        driversLicenseState: null,
        skillsAndQualifications: null,
        schooling: [
          this.schoolTemplate("High School"),
          this.schoolTemplate("College"),
          this.schoolTemplate("Other")
        ],
        references: [
          this.referenceTemplate(1),
          this.referenceTemplate(2),
          this.referenceTemplate(3),
        ],
        employmentHistory: [
          this.employmentHistoryTemplate(1),
          this.employmentHistoryTemplate(2),
          this.employmentHistoryTemplate(3),
          this.employmentHistoryTemplate(4)
        ],
        drawnSignatureData: null,
        signature: null,
        acceptApplicantStatement: false,
      }
    };
  },
  computed: {
    showTestDataButton() {
      return this.$route.query != null && this.$route.query.test != null && this.$route.query.test === "true";
    },
    ssnState() {
      return this.form.ssn != null ? this.form.ssn.toString().length == 9 : null;
    },
    phoneState() {
      return this.form.phone != null ? this.form.phone.toString().length == 10 : null;
    },
    otherPhoneState() {
      return (this.form.otherPhone != null && this.form.otherPhone != '') ? this.form.otherPhone.toString().length == 10 : null;
    },
    age() {
      let r = -1;
      if(this.form.dob != null) {
        r = Math.floor(moment.duration(moment().diff(moment(this.form.dob))).asYears());
      }
      return r;
    },
    signatureTitle() {
      return `By drawing and typing your name below you are giving ${this.sourceName} your digital signature and consent to perform a background check.`;
    }
  },
  methods: {
    onSubmit(evt) {
      evt.preventDefault();
      this.loading = true;
      this.form.drawnSignatureData = this.$refs.drawnSignature.save('image/jpeg');
      let payload = {
        senderInfo: {
          user:"qcfssystems@qcfreightservice.com",
          pass:"qcf5$yst3ms",
          from:"'QCFS Systems' <qcfssystems@qcfreightservice.com>",
          to:"jobs@qcfreightservice.com,jsmith@qcfreightservice.com",
          subject:`Application Submitted for ${this.form.firstName} ${this.form.lastName}`
        },
        data: JSON.parse(JSON.stringify(this.form)),
        template: "jobApplication.vue"
    };
    payload.data.source = this.source;
    payload.data.sourceName = this.sourceName;
    payload.data.age = this.age;
    axios.post(MAILER_URL, payload, { headers: { "Content-Type": "application/json" }})
      .then(() => {
        this.$refs.applicationForm.reset();
        this.loading = false;
        alert("Your application was submitted successfully, thank you!");
        this.$router.push("/");
      })
      .catch(() => alert("There was a problem submitting your application, please try submitting again."))
      .finally(() => this.loading = false);
    },
    employmentHistoryTemplate(num) {
      return {
        id: num,
        from: null,
        to: null,
        employer: null,
        phone: null,
        address: null,
        startTitle: null,
        endTitle: null,
        workSummary: null,
        supervisor: null,
        canContact: true,
        reasonForLeaving: null,
        startRate: null,
        endRate: null
      }
    },
    referenceTemplate(num) {
      return {
        id: num,
        name: null,
        phone: null,
        yearsKnown: null
      }
    },
    schoolTemplate(t) {
      return {
        type: t,
        name: null,
        location: null,
        yearsCompleted: null,
        graduated: false,
        degree: null,
        major: null
      }
    },
		clear(){
			this.$refs.drawnSignature.clear();
    },
    loadTestData() {
      this.form = this.testData();
    },
    testData() {
      return {
        positions: "positions",
        firstName: "test",
        middleName: "t",
        lastName: "test",
        dob: "2010-01-01",
        ssn: 111001111,
        address: "addr",
        city: "city",
        state: "st",
        zip: 11111,
        phone: 5551112222,
        otherPhone: 5558889999,
        email: "me@email.com",
        howHeardOfUs: "an ad",
        under18WorkPermit: false,
        noUnder18WorkPermitExplain: "i'm 10",
        employedBefore: true,
        employedBeforeSummary: "prior work summary",
        eligibleToWorkInUSA: true,
        dateAvailableToStart: "2020-04-20",
        desiredSalaryRange: "0-100",
        typesOfEmployment: [
  "Full Time",
  "Part Time",
  "Temporary",
  "Seasonal",
  "Educational Co-op"
],
        shiftPreferences: [
  "Any",
  "1st",
  "2nd",
  "3rd"
],
        canMeetAttendence: true,
        hasCommitedCrime: true,
        crimeDetails: "this one time, at band camp...",
        validCheckingOrSavings: true,
        driversLicenseNumber: "dl12345",
        driversLicenseState: "ia",
        skillsAndQualifications: "skills and quals",
        schooling: [
          {
            type: "High School",
            name: "hs",
            location: "hs loc",
            yearsCompleted: 4,
            graduated: true,
            degree: "diploma",
            major: "general"
          },
          {
            type: "College",
            name: "col",
            location: "col loc",
            yearsCompleted: 4,
            graduated: true,
            degree: "bachelors",
            major: "liberal arts"
          },
          {
            type: "Other",
            name: "trad",
            location: "trad loc",
            yearsCompleted: 2,
            graduated: true,
            degree: "certificate",
            major: "some trade"
          }
        ],
        references: [
          {
            id: 1,
            name: "joe",
            phone: 5551231234,
            yearsKnown: 4
          },
          {
            id: 2,
            name: "bob",
            phone: 5552342345,
            yearsKnown: 3
          },
          {
            id: 3,
            name: "mary",
            phone: 5553453456,
            yearsKnown: 2
          }
        ],
        employmentHistory: [
          {
            id: 1,
            from: "2015-01-01",
            to: "2015-06-01",
            employer: "job 1",
            phone: 5555551111,
            address: "job 1 addr",
            startTitle: "job 1 start title",
            endTitle: "job 1 end title",
            workSummary: "job 1 summary",
            supervisor: "job 1 super",
            canContact: true,
            reasonForLeaving: "job 1 reason",
            startRate: "job 1 start rate",
            endRate: "job 1 end rate"
          },
          {
            id: 2,
            from: "2014-01-01",
            to: "2014-06-01",
            employer: "job 2",
            phone: 5555552222,
            address: "job 2 addr",
            startTitle: "job 2 start title",
            endTitle: "job 2 end title",
            workSummary: "job 2 summary",
            supervisor: "job 2 super",
            canContact: true,
            reasonForLeaving: "job 2 reason",
            startRate: "job 2 start rate",
            endRate: "job 2 end rate"
          },
          {
            id: 3,
            from: "2013-01-01",
            to: "2013-06-01",
            employer: "job 3",
            phone: 5555553333,
            address: "job 3 addr",
            startTitle: "job 3 start title",
            endTitle: "job 3 end title",
            workSummary: "job 3 summary",
            supervisor: "job 3 super",
            canContact: true,
            reasonForLeaving: "job 3 reason",
            startRate: "job 3 start rate",
            endRate: "job 3 end rate"
          },
          {
            id: 4,
            from: "2012-01-01",
            to: "2012-06-01",
            employer: "job 4",
            phone: 5555554444,
            address: "job 4 addr",
            startTitle: "job 4 start title",
            endTitle: "job 4 end title",
            workSummary: "job 4 summary",
            supervisor: "job 4 super",
            canContact: true,
            reasonForLeaving: "job 4 reason",
            startRate: "job 4 start rate",
            endRate: "job 4 end rate"
          }
        ],
        drawnSignatureData: null,
        signature: "test t test",
        acceptApplicantStatement: true,
      };
    }
  }
}
</script>
